import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import DataTable from "../../core/DataTable.jsx";
import { useAppData } from "../../../context/AppDataContext";

const TaskSelector = ({
  selectedTasks,
  onTaskSelection,
  projectId,
  projectIds,
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { api } = useAppData();

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);

        const idsToUse =
          projectIds && projectIds.length > 0
            ? projectIds
            : projectId
            ? [projectId]
            : [];

        if (idsToUse.length === 0) {
          setTasks([]);
          setLoading(false);
          return;
        }

        const tasksPromises = idsToUse.map((id) =>
          api.task.getTasks({
            include: ["assigned_users", "status"],
            project_id: id,
          })
        );

        const tasksArrays = await Promise.all(tasksPromises);
        const allTasks = tasksArrays.flat();
        const uniqueTasks = Array.from(
          new Map(allTasks.map((task) => [task.task_id, task])).values()
        );

        setTasks(uniqueTasks);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId || (projectIds && projectIds.length > 0)) {
      fetchTasks();
    } else {
      setTasks([]);
      setLoading(false);
    }
  }, [api, projectId, projectIds]);

  const handleSelectedRowsChange = useCallback(
    ({ selectedRows }) => {
      onTaskSelection(selectedRows.map((row) => row.task_id));
    },
    [onTaskSelection]
  );

  const columns = [
    {
      name: "Select",
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedTasks.includes(row.task_id)}
          onChange={() => {
            const newSelectedTasks = selectedTasks.includes(row.task_id)
              ? selectedTasks.filter((id) => id !== row.task_id)
              : [...selectedTasks, row.task_id];
            onTaskSelection(newSelectedTasks);
          }}
        />
      ),
      width: "70px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status?.name || "No Status",
      sortable: true,
    },
    {
      name: "Priority",
      selector: (row) => row.priority,
      sortable: true,
      cell: (row) => (
        <span className={`priority priority-${row.priority}`}>
          {row.priority === "high"
            ? "High"
            : row.priority === "medium"
            ? "Medium"
            : row.priority === "low"
            ? "Low"
            : row.priority === "urgent"
            ? "Urgent"
            : row.priority === "critical"
            ? "Critical"
            : "None"}
        </span>
      ),
    },
    {
      name: "Points",
      selector: (row) => row.points_estimate || 0,
      sortable: true,
    },
    {
      name: "Assigned To",
      selector: (row) =>
        row.assigned_users
          ?.map((user) => `${user.first_name} ${user.last_name}`)
          .join(", ") || "Unassigned",
      sortable: true,
    },
  ];

  return (
    <div className="task-selector">
      <DataTable
        columns={columns}
        data={tasks}
        loading={loading}
        noDataComponent={
          projectId || (projectIds && projectIds.length > 0)
            ? "No tasks found"
            : "Select projects to view tasks"
        }
      />
    </div>
  );
};

TaskSelector.propTypes = {
  selectedTasks: PropTypes.arrayOf(PropTypes.string).isRequired,
  onTaskSelection: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  projectIds: PropTypes.arrayOf(PropTypes.string),
};

export default TaskSelector;
